import { useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import Icon from '../Icon'
import type { IconName } from '../Icon'
import { TAB_BAR_INSET } from './TabBar'

/**
 * The phone navigation bar.
 *
 * Circular back button, centred title, one circular action — the iOS shape.
 * The buttons are circles rather than bare glyphs because a 44pt circular
 * target is obvious at the edge of a screen where a bare chevron is not.
 */
export default function MobileHeader({
  title,
  back = true,
  action,
  transparent = false,
}: {
  title?: string
  /** `true` goes back one entry; a string navigates to that path. */
  back?: boolean | string
  action?: { icon: IconName; label: string; onClick: () => void; accent?: boolean }
  /** Sits over a hero image — no bar fill, buttons carry their own surface. */
  transparent?: boolean
}) {
  const navigate = useNavigate()

  return (
    <header
      style={{
        position: transparent ? 'absolute' : 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        display: 'grid',
        gridTemplateColumns: '44px minmax(0, 1fr) 44px',
        alignItems: 'center',
        gap: 8,
        padding: '10px var(--gutter)',
        paddingTop: 'max(10px, env(safe-area-inset-top))',
        background: transparent ? 'transparent' : 'var(--bg)',
        borderBottom: transparent ? 'none' : 'var(--hairline-width) solid var(--hairline)',
      }}
    >
      {back ? (
        <CircleButton
          icon="chevronLeft"
          label="Back"
          onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))}
        />
      ) : (
        <span />
      )}

      <h1
        className="truncate"
        style={{
          textAlign: 'center',
          fontSize: '1.05rem',
          fontWeight: 700,
          // Transparent headers sit on artwork, which is dark in every skin.
          color: transparent ? '#fff' : 'var(--text-primary)',
          textShadow: transparent ? '0 1px 8px rgb(0 0 0 / 0.5)' : undefined,
        }}
      >
        {title}
      </h1>

      {action ? (
        <CircleButton
          icon={action.icon}
          label={action.label}
          onClick={action.onClick}
          accent={action.accent}
        />
      ) : (
        <span />
      )}
    </header>
  )
}

export function CircleButton({
  icon,
  label,
  onClick,
  accent,
}: {
  icon: IconName
  label: string
  onClick: () => void
  accent?: boolean
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: 'var(--panel)',
        border: 'var(--hairline-width) solid var(--hairline)',
        color: accent ? 'var(--accent-text)' : 'var(--text-primary)',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <Icon name={icon} size={19} strokeWidth={2} />
    </button>
  )
}

/** Wraps a mobile screen's scrolling content with the right tab-bar clearance. */
export function MobileScreen({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: '0 var(--gutter)', paddingBottom: TAB_BAR_INSET }}>
      <div className="stack" style={{ gap: 22 }}>
        {children}
      </div>
    </div>
  )
}

/** Small uppercase label that opens a section. */
export function SectionLabel({
  children,
  trailing,
}: {
  children: ReactNode
  trailing?: ReactNode
}) {
  return (
    <div className="spread" style={{ paddingInline: 2 }}>
      <span className="t-eyebrow">{children}</span>
      {trailing}
    </div>
  )
}
